import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import {Subscription} from 'rxjs';
import {BookingProviderDetails} from '../../types/booking-provider-details';
import {BookingState} from '../../types/booking-state';
import {BookingV2ManagerService} from '../../services/booking-v2-manager.service';
import {BookingV2StateService} from '../../services/booking-v2-state.service';
import {ToolsService} from '../../../../services/tools.service';
import {BookingV2DataService} from '../../services/booking-v2-data.service';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';
import {QueryParams} from '../../../../types/QueryParams';
import {BookingV2RouteResolverService} from '../../services/booking-v2-route-resolver.service';
import {BookingStepsEnum} from '../../enums/booking-enums';
import {filter} from 'rxjs/operators';
import {BookingV2SurveyService} from '../../services/booking-v2-survey.service';
import {ScriptNameEnum} from '../../../../enums/SsciptsEnums';
import {CodeSnippetsService} from '../../../../services/code-snippets.service';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-booking-v2',
  templateUrl: './booking-v2.component.html',
  styleUrls: ['./booking-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BookingV2Component implements OnInit, OnDestroy {
  public bookingStepsEnum = BookingStepsEnum;
  public bookingStep = BookingStepsEnum.SERVICES;
  public providerDetails: BookingProviderDetails;
  public errorMessage: string = null;
  public showSummary = false;
  public containerCssClassName: string;
  public state: BookingState;
  private previousStep: number;
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private bookingManager: BookingV2ManagerService,
    private bookingState: BookingV2StateService,
    private bookingData: BookingV2DataService,
    private bookingRouteResolver: BookingV2RouteResolverService,
    private bookingSurvey: BookingV2SurveyService,
    private _renderer2: Renderer2,
    private headerCodeSnippets: CodeSnippetsService,
    private _change: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadHubspotScripts();
    }
    this.bookingData.setNoFollowMetaTag();
    this.bookingManager.initialStep = true;
    this.handleRouteChange(false);
    this.setListeners();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.bookingSurvey.resetAll();
    this.bookingData.resetAll();
    this.bookingManager.resetAll();
    this.bookingState.resetState();
  }

  private handleProviderDetails(providerData: BookingProviderDetails): void {
    this.providerDetails = providerData;
    this._change.markForCheck();
  }

  private setContainerCssClassName(step: number): void {
    this.containerCssClassName = `booking-step-${step}`;
  }

  private handleBookingState(state: BookingState): void {
    this.state = state;
    this.bookingStep = state.step;
    this.showSummary = state.showSummary;
    this.setContainerCssClassName(state.step);
    if (this.bookingStep !== this.previousStep) {
      this.scrollToTop();
      this.previousStep = this.bookingStep;
    }
    this._change.markForCheck();
  }

  private handleRouteChange(isRedirected: boolean): void {
    const params: Params = this.route.snapshot.params;
    const queryParams: QueryParams = this.route.snapshot.queryParams;
    this.bookingRouteResolver.onRouteChange(params, queryParams, isRedirected);
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const isRedirected: boolean = this.router.getCurrentNavigation()?.extras?.replaceUrl;
          this.handleRouteChange(isRedirected);
        }
      }),
      this.bookingState.state$.subscribe((result) => this.handleBookingState(result)),
      this.bookingData.providerDetails$
        .pipe(filter((res) => res !== null))
        .subscribe((result) => this.handleProviderDetails(result)),
    );
  }

  private loadHubspotScripts(): void {
    const hsScript: string = '//js.hsforms.net/forms/shell.js';
    this.headerCodeSnippets.loadExternalScript(this._renderer2, hsScript, ScriptNameEnum.HUBSPOT_SHELL);
    const hsPageScript: string = '//js.hs-scripts.com/7080361.js';
    this.headerCodeSnippets.loadExternalScript(this._renderer2, hsPageScript, ScriptNameEnum.HUBSPOT_WEBSITE);
  }

  private scrollToTop(): void {
    this.tools.scrollTo();
  }
}
